import { Spin } from 'antd'
import { toNumber } from 'lodash-es'
import isEmpty from 'lodash-es/isEmpty'
import React, { FunctionComponent } from 'react'
import styled, { useTheme } from 'styled-components'
import { statesWithoutHoverAndActive } from '@/components/auto-report/summary/summary-config'
import { ApexChartOptions, ApexCharts } from '@/components/common/apex-charts'
import Blur from '@/components/common/blur'
import AnalyzeFeedback from '@/components/kol/analyze-feedback'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import Blocker from '@/components/kol/detail/blocker'
import { NoData } from '@/components/kol/detail/no-data'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Audience } from '@/store/kol-detail'
import { FeedbackType } from '@/types/api/feedback'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import {
  ampli,
  ClickReportAudienceAuthenticityAccuracyProperties,
} from '@/utils/ampli'
import { Platform } from '@/utils/convert-platform'

interface AudienceAuthenticityProps {
  kolInfo?: KolSchemaModel
  authenticity?: Audience['authenticity']
  isFetching: boolean
  isLocked: boolean
  showMockData?: boolean
  isPublicDetailPage: boolean
  platform: Platform
}

const AudienceAuthenticity: FunctionComponent<AudienceAuthenticityProps> = ({
  authenticity,
  kolInfo,
  isFetching,
  isLocked,
  showMockData,
  isPublicDetailPage,
  platform,
}) => {
  const { formatNumber, formatMessage } = useIntl()
  const theme = useTheme()
  const realPercentage = toNumber(authenticity?.real) * 100
  const otherPercentage = 100 - realPercentage

  const options: ApexChartOptions = {
    chart: {
      height: 200,
      id: 'audience-authenticity',
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
        donut: {
          size: '75%',
          labels: {
            show: true,
            value: {
              fontSize: '22px',
              fontWeight: 600,
              color: theme.colors.base.grey.grey1,
              offsetY: 3,
            },
            total: {
              show: true,
              showAlways: true,
              fontSize: '13px',
              fontWeight: 400,
              color: theme.colors.base.grey.grey2,
              label: formatMessage({
                id: 'kol:audience_analysis_authenticity',
              }),
              formatter: function () {
                return formatNumber(realPercentage / 100, {
                  isPercentageDisplay: true,
                })
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      custom: function ({ series, seriesIndex, w }) {
        const label = w.config.labels?.[seriesIndex] ?? ''
        const value = series[seriesIndex].toFixed(1)

        return `
            <div class="tooltip">
              <div class="marker"></div>
              <span class="label">${label}：${value}%</span>
            </div>
        `
      },
    },
    legend: {
      show: true,
      position: 'right',
      horizontalAlign: 'left',
      offsetY: 50,
      fontSize: '14px',
      markers: {
        width: 14,
        height: 14,
        offsetX: -5,
        offsetY: 2,
      },
      onItemClick: {
        toggleDataSeries: false,
      },
      onItemHover: {
        highlightDataSeries: false,
      },
    },
    labels: [
      formatMessage({ id: 'kol:audience_analysis_authenticity_real' }),
      formatMessage({ id: 'kol:edit_field_gender_other' }),
    ],
    colors: [theme.colors.brand.secondary, theme.colors.base.grey.grey5],
    stroke: {
      colors: ['#EFF7FF'],
      curve: 'smooth',
      width: 3,
    },
    states: statesWithoutHoverAndActive,
  }

  return (
    <KolDetailDataBlock
      hoverableExtraContent
      colSpan={12}
      header={{
        poweredByAI: true,
        title: formatMessage(
          {
            id: 'kol:audience_analysis_authenticity',
          },
          { platform: platform.title },
        ),
        tooltip: {
          title: formatMessage({ id: 'kol:audience_analysis_data_source' }),
        },
        extraContent: (
          <AnalyzeFeedback
            feedbackEvent={(
              eventProps: ClickReportAudienceAuthenticityAccuracyProperties,
            ): void => {
              ampli.clickReportAudienceAuthenticityAccuracy(eventProps)
            }}
            feedbackType={FeedbackType.AudienceAuthenticity}
            kolInfo={kolInfo}
            platform={platform.shortcode}
          />
        ),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Audience Authenticity'
      kolName={kolInfo?.name}
      loading={isFetching}
      platform={platform.shortcode}
    >
      <Spin indicator={<Blocker />} spinning={isPublicDetailPage}>
        <Blur activate={isLocked && !showMockData}>
          {isEmpty(authenticity) ? (
            <NoData />
          ) : (
            <StyledChart>
              <ApexCharts
                options={options}
                series={[realPercentage, otherPercentage]}
                type='donut'
              />
            </StyledChart>
          )}
        </Blur>
      </Spin>
    </KolDetailDataBlock>
  )
}

const StyledChart = styled.div`
  .tooltip {
    color: ${({ theme }): string => theme.colors.base.grey.grey1};
    background: ${({ theme }): string => theme.colors.border.white};
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 8px;
    border-radius: 4px;
    box-shadow: 0px 2px 12px 0px rgba(0, 0, 0, 0.1);
    font-size: 12px;

    .marker {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #78b8fa;
    }
  }

  .apexcharts-legend-series {
    display: flex;
    gap: 8px;
  }
`

export default AudienceAuthenticity
